import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
} from "react";
import { User, CognitoClaim, UserRole } from "../models/userModels";

export const baseUserState: User = {
  isAuthenticated: false,
  sub: "",
  [CognitoClaim.TENANT_NAME]: "",
  iss: "",
  [CognitoClaim.USERNAME]: "",
  [CognitoClaim.TENANT_ID]: "",
  given_name: "",
  origin_jti: "",
  aud: "",
  event_id: "",
  token_use: "",
  auth_time: 0,
  exp: 0,
  [CognitoClaim.ROLE]: "",
  iat: 0,
  family_name: "",
  [CognitoClaim.ENVIRONMENT]: "",
  jti: "",
  email: "",
};

interface UserContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  isRestrictedUser: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(baseUserState);

  // Roles with restricted access (e.g., no side menu, limited features)
  const RESTRICTED_ROLES: UserRole[] = [UserRole.MANAGER_UPLOAD];

  const isRestrictedUser = useMemo(
    () => RESTRICTED_ROLES.includes(user?.[CognitoClaim.ROLE] as UserRole),
    [user]
  );

  return (
    <UserContext.Provider value={{ user, setUser, isRestrictedUser }}>
      {children}
    </UserContext.Provider>
  );
};
